/**
 * The Data panel's Preparation part: the saved Transform pipelines the
 * dashboard applies, in the order they run, with add, reorder, remove, and
 * a way back to each one in the console.
 */
import React, { useCallback, useMemo } from "react"
import { Button, Select } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowDown,
  faArrowUp,
  faArrowUpRightFromSquare,
  faTrash,
} from "@fortawesome/free-solid-svg-icons"
import { useNavigate } from "react-router-dom"
import { ROUTES } from "constants/routes"
import { PROJECT_TAB_KEYS } from "constants/projectTabs"
import { useProjectStore } from "stores/projectStore"
import { useQueryBuilderStore } from "stores/queryBuilderStore"
import { useQueryConsoleLayoutStore } from "stores/queryConsoleLayoutStore"
import { notify } from "shared/ui/Notifications/notify"
import type {
  TransformPipeline,
  TransformStep,
} from "types/queryBuilder.types"
import { stepSummary } from "features/QueryConsole/components/Transform/internal/stepSummary"
import { usePipelines } from "features/Dashboard/api/usePipelines"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"

/** The project page's Data Reporting tab, where the console lives. */
const CONSOLE_TAB = PROJECT_TAB_KEYS.DATA_REPORTING
export const NOT_LOADED_IN_CONSOLE =
  "Pipeline not loaded in the console yet; pick it from the Transform list."

const stepText = (step: TransformStep): string =>
  stepSummary(step)
    .map((t) => t.value)
    .join(" ")

/** A pipeline in one line: how many steps, and what the first two do. */
export const pipelineSummary = (pipeline: TransformPipeline): string => {
  const { steps } = pipeline
  if (steps.length === 0) return "No steps"
  const count = `${steps.length} step${steps.length === 1 ? "" : "s"}`
  const shown = steps.slice(0, 2).map(stepText).join("; ")
  return `${count}: ${shown}${steps.length > 2 ? "; and more" : ""}`
}

/**
 * Opens the console's Transform view on the project page, with pipeline
 * `name` selected when the console already holds it for this project.
 * Otherwise the view opens as it is (the console lists the project's saved
 * pipelines once it loads) and a note says to pick it there. `beforeLeave`
 * runs first, so the panel keeps an edit that is still a draft.
 */
const useOpenInConsole = (
  beforeLeave: () => void
): ((name: string | null) => void) => {
  const navigate = useNavigate()
  const projectId = useDashboardStore((s) => s.projectId)
  return useCallback(
    (name: string | null) => {
      beforeLeave()
      const builder = useQueryBuilderStore.getState()
      // The console keeps one project's pipelines; another project's are
      // replaced when the console opens this one.
      const local =
        name !== null && builder.projectId === projectId
          ? builder.transformPipelines.find((p) => p.name === name)
          : undefined
      if (local) builder.setActiveTransformPipeline(local.id)
      else if (name !== null) notify.toast.info(NOT_LOADED_IN_CONSOLE)
      useQueryConsoleLayoutStore.getState().setViewMode("transform")
      navigate(`${ROUTES.PROJECT(projectId)}?tab=${CONSOLE_TAB}`)
    },
    [beforeLeave, navigate, projectId]
  )
}

const PreparationField: React.FC<{ beforeLeave: () => void }> = ({
  beforeLeave,
}) => {
  const project = useProjectStore((s) => s.project)
  const pipelines = usePipelines(project.id, project.Created_by)
  const data = useDashboardStore((s) => s.document.data)
  const setData = useDashboardStore((s) => s.setData)
  const openInConsole = useOpenInConsole(beforeLeave)
  const saved = pipelines.data
  const byName = useMemo(
    () => new Map((saved ?? []).map((p) => [p.name, p])),
    [saved]
  )
  const chosen = data.pipelines
  const setChosen = (next: string[]): void =>
    setData({ ...data, pipelines: next })
  const move = (from: number, by: -1 | 1): void => {
    const next = [...chosen]
    const [name] = next.splice(from, 1)
    next.splice(from + by, 0, name)
    setChosen(next)
  }
  const addable = (saved ?? []).filter((p) => !chosen.includes(p.name))

  return (
    <section className="dq-data__part" aria-labelledby="dq-data-prep">
      <h3 id="dq-data-prep" className="dq-data__title">
        Preparation
      </h3>
      <p className="dq-field__hint">
        Banding, merged variables, &quot;any of&quot; nets and computed scores
        are Transform steps built in the console. Save them as a pipeline, then
        add it here.
      </p>
      {chosen.length === 0 ? (
        <div className="dq-prep__empty">
          <span>No pipelines applied.</span>
          <Button
            type="link"
            size="small"
            className="dq-prep__console"
            onClick={() => openInConsole(null)}
          >
            Open the console
          </Button>
        </div>
      ) : (
        <ol className="dq-prep" aria-label="Applied pipelines">
          {chosen.map((name, i) => {
            const found = byName.get(name)
            const missing = pipelines.isSuccess && !found
            return (
              <li
                key={`${i}:${name}`}
                className="dq-prep__row"
                data-missing={missing || undefined}
              >
                <span className="dq-prep__order" aria-hidden>
                  {i + 1}
                </span>
                <div className="dq-prep__main">
                  <span className="dq-prep__name" title={name}>
                    {name}
                  </span>
                  {missing ? (
                    <span className="dq-data__missing">not found</span>
                  ) : (
                    found && (
                      <span className="dq-prep__summary">
                        {pipelineSummary(found.pipeline)}
                      </span>
                    )
                  )}
                </div>
                <div className="dq-prep__actions">
                  <Button
                    size="small"
                    type="text"
                    aria-label={`Open ${name} in the console`}
                    title="Open in console"
                    disabled={missing}
                    icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                    onClick={() => openInConsole(name)}
                  />
                  <Button
                    size="small"
                    type="text"
                    aria-label={`Move ${name} up`}
                    title="Run earlier"
                    disabled={i === 0}
                    icon={<FontAwesomeIcon icon={faArrowUp} />}
                    onClick={() => move(i, -1)}
                  />
                  <Button
                    size="small"
                    type="text"
                    aria-label={`Move ${name} down`}
                    title="Run later"
                    disabled={i === chosen.length - 1}
                    icon={<FontAwesomeIcon icon={faArrowDown} />}
                    onClick={() => move(i, 1)}
                  />
                  <Button
                    size="small"
                    type="text"
                    danger
                    aria-label={`Remove ${name}`}
                    title="Remove"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => setChosen(chosen.filter((_, j) => j !== i))}
                  />
                </div>
              </li>
            )
          })}
        </ol>
      )}
      <Select
        showSearch
        size="small"
        className="dq-prep__add"
        classNames={{ popup: { root: "dq-workspace dq-prep-dropdown" } }}
        aria-label="Add a pipeline"
        placeholder="Add a saved pipeline"
        value={null}
        loading={pipelines.isLoading}
        options={addable.map((p) => ({
          value: p.name,
          label: p.name,
          summary: pipelineSummary(p.pipeline),
        }))}
        optionFilterProp="label"
        optionRender={(option) => (
          <div className="dq-prep__option">
            <span className="dq-prep__option-name">{option.data.label}</span>
            <span className="dq-prep__option-summary">
              {option.data.summary}
            </span>
          </div>
        )}
        notFoundContent={
          pipelines.isLoading
            ? "Loading the saved pipelines"
            : (saved ?? []).length === 0
              ? "This project has no saved pipelines yet"
              : "Every saved pipeline is applied"
        }
        onChange={(name: string | null) => {
          if (name && !chosen.includes(name)) setChosen([...chosen, name])
        }}
      />
      {pipelines.isError && (
        <p className="dq-field__hint" role="alert">
          The saved pipelines could not be loaded.
        </p>
      )}
    </section>
  )
}

export default PreparationField
